
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { format, addMonths, addYears } from "date-fns";
import { CalendarIcon, LockIcon, RefreshCw } from "lucide-react";

import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { ContractType } from "@/lib/types";
import { ContractFormValues } from "../ContractFormSchema";

interface ContractDatesSectionProps {
  form: UseFormReturn<ContractFormValues>;
  selectedContractType: ContractType | null;
  renewalDate: Date | null;
}

export function ContractDatesSection({ 
  form, 
  selectedContractType, 
  renewalDate 
}: ContractDatesSectionProps) {
  const { t } = useTranslation();
  const [selectedDuration, setSelectedDuration] = useState<string>("1year");
  const [durationOptions, setDurationOptions] = useState<{ value: string; label: string }[]>([
    { value: "1month", label: t("1 month") },
    { value: "3months", label: t("3 months") },
    { value: "6months", label: t("6 months") },
    { value: "1year", label: t("1 year") },
    { value: "2years", label: t("2 years") },
    { value: "3years", label: t("3 years") },
    { value: "5years", label: t("5 years") },
    { value: "custom", label: t("Custom date") },
  ]);

  const addDuration = (date: Date, durationValue: string): Date => {
    switch (durationValue) {
      case "1month":
        return addMonths(date, 1);
      case "3months":
        return addMonths(date, 3);
      case "6months":
        return addMonths(date, 6);
      case "1year":
        return addYears(date, 1);
      case "2years":
        return addYears(date, 2);
      case "3years":
        return addYears(date, 3);
      case "5years":
        return addYears(date, 5);
      default:
        return date;
    }
  };

  useEffect(() => {
    setDurationOptions([
      { value: "1month", label: t("1 month") },
      { value: "3months", label: t("3 months") },
      { value: "6months", label: t("6 months") },
      { value: "1year", label: t("1 year") },
      { value: "2years", label: t("2 years") },
      { value: "3years", label: t("3 years") },
      { value: "5years", label: t("5 years") },
      { value: "custom", label: t("Custom date") },
    ]);
  }, [t]);

  useEffect(() => {
    const signedDate = form.watch("signedDate");
    if (signedDate) {
      if (selectedContractType && selectedContractType.auto_renews) {
        // Auto-renewal calculation is handled in the parent component
      } else if (selectedDuration !== "custom") {
        const newExpiryDate = addDuration(signedDate, selectedDuration);
        form.setValue("expiryDate", newExpiryDate);
      }
    }
  }, [form.watch("signedDate"), selectedContractType, selectedDuration]);

  useEffect(() => {
    const signedDate = form.watch("signedDate");
    const expiryDate = form.watch("expiryDate");
    
    if (selectedContractType?.auto_renews) return;
    
    if (signedDate && expiryDate) {
      const diffInDays = Math.round((expiryDate.getTime() - signedDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (Math.abs(diffInDays - 30) <= 5) {
        setSelectedDuration("1month");
      } else if (Math.abs(diffInDays - 90) <= 5) {
        setSelectedDuration("3months");
      } else if (Math.abs(diffInDays - 180) <= 5) {
        setSelectedDuration("6months");
      } else if (Math.abs(diffInDays - 365) <= 10) {
        setSelectedDuration("1year");
      } else if (Math.abs(diffInDays - 730) <= 15) {
        setSelectedDuration("2years");
      } else if (Math.abs(diffInDays - 1095) <= 15) {
        setSelectedDuration("3years");
      } else if (Math.abs(diffInDays - 1825) <= 15) {
        setSelectedDuration("5years");
      } else {
        setSelectedDuration("custom");
      }
    }
  }, []);

  // Format the renewal date safely
  const formattedRenewalDate = renewalDate && renewalDate instanceof Date && !isNaN(renewalDate.getTime())
    ? format(renewalDate, "MMMM d, yyyy")
    : null;

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
      <FormField
        control={form.control}
        name="signedDate"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("Signed Date")}</FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full pl-3 text-left font-normal",
                      !field.value && "text-muted-foreground"
                    )}
                  >
                    {field.value ? (
                      format(field.value, "PPP")
                    ) : (
                      <span>{t("Pick a date")}</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={field.value}
                  onSelect={field.onChange}
                  disabled={(date) =>
                    date > new Date() || date < new Date("1900-01-01")
                  }
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="space-y-2">
        <FormField
          control={form.control}
          name="expiryDate"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel className="flex items-center gap-1">
                {selectedContractType?.auto_renews 
                  ? t('Renewal Date') 
                  : t('Expiry Date')}
                {selectedContractType?.auto_renews && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <LockIcon className="h-3 w-3 text-blue-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        {t("This date is locked because it's determined by the auto-renewal settings of the contract type")}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </FormLabel>
              
              {!selectedContractType?.auto_renews && (
                <div className="mb-2">
                  <Select
                    value={selectedDuration}
                    onValueChange={(value) => {
                      setSelectedDuration(value);
                      if (value !== "custom" && field.value) {
                        const newExpiryDate = addDuration(form.watch("signedDate"), value);
                        form.setValue("expiryDate", newExpiryDate);
                      }
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t("Select duration")} />
                    </SelectTrigger>
                    <SelectContent>
                      {durationOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full pl-3 text-left font-normal",
                        !field.value && "text-muted-foreground",
                        selectedContractType?.auto_renews && "bg-blue-50 border-blue-200 cursor-not-allowed dark:bg-blue-950 dark:border-blue-800"
                      )}
                      disabled={selectedContractType?.auto_renews}
                    >
                      {field.value ? (
                        format(field.value, "PPP")
                      ) : (
                        <span>{t("Pick a date")}</span>
                      )}
                      {!selectedContractType?.auto_renews && (
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      )}
                      {selectedContractType?.auto_renews && (
                        <LockIcon className="ml-auto h-4 w-4 text-blue-500" />
                      )}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                {!selectedContractType?.auto_renews && (
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setSelectedDuration("custom");
                      }}
                      disabled={(date) => date < new Date("1900-01-01")}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                )}
              </Popover>
              
              {selectedContractType?.auto_renews && formattedRenewalDate && (
                <FormDescription className="flex items-center text-blue-500 mt-2">
                  <RefreshCw className="h-3 w-3 mr-1" />
                  <span>
                    {t("Will auto-renew on {{date}}", { 
                      date: formattedRenewalDate
                    })}
                  </span>
                </FormDescription>
              )}
              
              {selectedContractType?.auto_renews && (
                <FormDescription className="text-muted-foreground text-xs mt-1">
                  {t("This date is set automatically based on contract type renewal settings")}
                </FormDescription>
              )}
              
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
